import Calendar from '@ixigo/icons/Calendar';
import LocationOn from '@ixigo/icons/LocationOn';
import Swap from '@ixigo/icons/Swap';
import Button from '@ixigo/iui/Button';
import IconButton from '@ixigo/iui/ButtonBase';
import { toast } from '@ixigo/iui/Toast';
import { DisplayField } from '@repo/shared/components';
import cn from '@repo/shared/utils/cn';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
	DateTimePickerSheet,
	formatDateTimeLabel,
} from '@/components/date-time-picker';
import {
	LocationPickerSheet,
	type LocationPickerSheetProps,
} from '@/lib/location';
import { useModalState } from '@/lib/navigation/useModalState';
import { isAfterMinimumDuration } from '@/routes/-home/-utils/date-validation';
import { fetchTripType, type TripTypeResponse } from './api';
import styles from './SearchForm.module.css';
import { useSearchFormActions, useSearchFormData } from './store/selectors';
import type { FormValidationResult, SearchFormData } from './store/types';
import { searchTracker } from './tracker';

export interface SearchFormProps
	extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSubmit'> {
	initialData?: Partial<SearchFormData>;
	onSubmit: (validationResult: FormValidationResult) => void;
}

export function SearchForm({
	initialData,
	onSubmit,
	...props
}: SearchFormProps) {
	const formData = useSearchFormData();
	const { pickup, drop: destination } = formData;
	const actions = useSearchFormActions();

	const [animateSwap, setAnimateSwap] = useState(false);
	const [tripTypeInfo, setTripTypeInfo] = useState<
		TripTypeResponse['data'] | null
	>(null);
	const [swapPhase, setSwapPhase] = useState<
		'idle' | 'swapping' | 'completing'
	>('idle');

	// UI state for location picker modal (boolean)
	const { value: pickerOpen, setOpen: setPickerOpen } = useModalState('loc');
	const defaultActiveFieldRef =
		useRef<LocationPickerSheetProps['activeField']>(undefined);

	const [showDateTimePicker, setShowDateTimePicker] = useState(false);
	const autoOpenCallbackRef = useRef<() => void>(null);

	// Initialize form data if provided
	useEffect(() => {
		if (initialData) {
			actions.updateFormData(initialData);
		}
	}, [initialData, actions]);

	// Check if swap should be enabled - at least one field should have a value
	// This prevents swapping when both fields are empty (no point in swapping nothing)
	const swapEnabled = !!(pickup || destination);

	// Format the ISO date-time string into a readable label (e.g., "Mon, 23 Jul • 6:30 AM")
	const formattedDateTime = useMemo(
		() => formatDateTimeLabel(formData.dateTime),
		[formData.dateTime],
	);

	const handleSwap = () => {
		if (swapPhase !== 'idle') return; // Prevent multiple clicks during animation

		setAnimateSwap((prev) => !prev);
		setSwapPhase('swapping');
	};

	// Handle animation end for the swap movement
	const handleSwapAnimationEnd = () => {
		if (swapPhase === 'swapping') {
			// Swap the data after fields have moved to their new positions
			actions.swapLocations();
			setSwapPhase('completing');
		}
	};

	// Handle animation end for the completion phase
	const handleCompleteAnimationEnd = () => {
		if (swapPhase === 'completing') {
			setSwapPhase('idle');
		}
	};

	const handleSubmit = (updatedFormData?: SearchFormData) => {
		const { pickup, drop, dateTime } = updatedFormData || formData;
		if (!pickup) {
			handlePickupClick();
			return;
		}
		if (!drop) {
			handleDestinationClick();
			return;
		}
		const pickupTime = dateTime;
		// if pickupTime is half hour past from now, show error
		const isAfterAllowableDuration = isAfterMinimumDuration(
			pickupTime,
			tripTypeInfo?.minDurationInMinutes || 30,
		);
		if (!isAfterAllowableDuration) {
			toast.error(
				`Please select a time after ${tripTypeInfo?.minDurationInMinutes || 30} minutes`,
				{
					position: 'bottom-center',
					duration: 2000,
				},
			);
			openDateTimePicker();
			return;
		}
		const validationResult = actions.validateForm();
		if (validationResult.isValid) {
			searchTracker.tracker.search();
		}
		onSubmit(validationResult);
	};

	const handlePickupClick = () => {
		defaultActiveFieldRef.current = 'pickup';
		setPickerOpen({ open: true });
	};

	const handleDestinationClick = () => {
		defaultActiveFieldRef.current = 'drop';
		setPickerOpen({ open: true });
	};

	const updateTripTypeInfo = () => {
		const { pickup, drop } = formData;
		if (!pickup || !drop) {
			return;
		}
		fetchTripType({ pickup, drop }).then((response) => {
			if (!('errors' in response)) {
				setTripTypeInfo(response.data);
			}
		});
	};

	const toggleDateTimePicker = () => {
		if (!showDateTimePicker) {
			updateTripTypeInfo();
		}
		setShowDateTimePicker((prev) => !prev);
	};

	const openDateTimePicker = () => {
		updateTripTypeInfo();
		setShowDateTimePicker(true);
	};

	return (
		<div className="bg-primary mx-20 w-auto rounded-20 relative" {...props}>
			<div className="border border-tertiary rounded-t-20 overflow-hidden">
				<div
					className={cn(
						styles.animatedField,
						swapPhase === 'swapping' && styles.swapPickupDown,
						swapPhase === 'completing' && styles.swapComplete,
					)}
					onAnimationEnd={
						swapPhase === 'swapping'
							? handleSwapAnimationEnd
							: handleCompleteAnimationEnd
					}
				>
					<DisplayField
						icon={<LocationOn />}
						value={pickup?.mainText}
						placeholder="Pickup Location"
						onClick={handlePickupClick}
						className="px-15px"
					/>
				</div>
				<div className="h-0 border-b border-disabled" />

				<div
					className={cn(
						styles.animatedField,
						swapPhase === 'swapping' && styles.swapDestinationUp,
						swapPhase === 'completing' && styles.swapComplete,
					)}
				>
					<DisplayField
						icon={<LocationOn />}
						value={destination?.mainText}
						placeholder="Drop-off Location"
						onClick={handleDestinationClick}
						className="px-15px"
					/>
				</div>
				<div className="h-0 border-b border-disabled" />

				<DisplayField
					icon={<Calendar />}
					value={formattedDateTime}
					placeholder="Pickup Date & Time"
					onClick={toggleDateTimePicker}
					className="px-15px"
				/>
			</div>

			<Button
				variant="solid"
				color="brand"
				size="lg"
				className="w-full rounded-b-20 rounded-t-none"
				onClick={() => handleSubmit()}
			>
				Book a Cab
			</Button>

			<IconButton
				onClick={handleSwap}
				className="absolute z-10 right-15 top-9 size-30px rounded-full bg-primary hover:bg-primary border border-secondary flex items-center justify-center"
				aria-label="Swap pickup and destination"
				disabled={!swapEnabled}
			>
				<Swap
					className={cn(
						'w-6 h-6 transition-transform duration-300',
						swapEnabled ? 'text-primary' : 'text-disabled',
						animateSwap ? 'rotate-[450deg]' : 'rotate-90',
					)}
				/>
			</IconButton>
			<LocationPickerSheet
				open={Boolean(pickerOpen)}
				onClose={() => {
					setPickerOpen({ open: false });
					defaultActiveFieldRef.current = undefined;
				}}
				activeField={defaultActiveFieldRef.current}
				defaultValue={{ pickup: formData.pickup, drop: formData.drop }}
				onComplete={({ pickup, drop }) => {
					actions.updateFormData({
						pickup: pickup,
						drop: drop,
					});
					if (!formData.dateTime) {
						autoOpenCallbackRef.current = openDateTimePicker;
					}
				}}
				TransitionProps={{
					onExited: () => {
						const callback = autoOpenCallbackRef.current;
						autoOpenCallbackRef.current = null;
						setTimeout(() => {
							callback?.();
						});
					},
				}}
				mapApiKey={import.meta.env.PUBLIC_MAP_KEY}
			/>
			<DateTimePickerSheet
				open={showDateTimePicker}
				onClose={toggleDateTimePicker}
				defaultValue={formData.dateTime}
				minDurationInMinutes={tripTypeInfo?.minDurationInMinutes}
				alertMsg={tripTypeInfo?.msg}
				onConfirm={(value) => {
					const updatedFormData = actions.updateFormData({
						dateTime: value.toISOString(),
					});
					setShowDateTimePicker(false);
					handleSubmit(updatedFormData);
				}}
			/>
		</div>
	);
}
